import { useFormTypedWithPartial } from "shared/app/LeadsTableView/hooks/useFormTypedWithPartial";
import { array, object, string } from "yup";

export interface CreateLeadPayload {
  first_name: string;
  last_name: string;
  property_id?: number | null;
  unit_id?: number | null;
  primary_phone?: string;
  primary_email?: string;
  other_phones: string[];
  other_emails: string[];
  message: string;
  desired_move_in_date: string;
  source_id?: string;
  partner_user_id?: number | null;
}

const validationSchema = object({
  first_name: string().required("This field is required"),
  last_name: string().required("This field is required"),
  primary_phone: string()
    .required("This field is required")
    .length(10, "Invalid phone number"),
  primary_email: string().required("This field is required"),
  other_phones: array().of(
    string()
      .required("This field is required")
      .length(10, "Invalid phone number")
  ),
  other_emails: array().of(
    string().required("This field is required").email("Invalid email address")
  ),
  desired_move_in_date: string().optional(),
  message: string().optional(),
  property_id: string().nullable().optional(),
  unit_id: string().nullable().optional(),
  source_id: string().optional(),
  partner_user_id: string().nullable().optional()
});

export const useFormController = ({
  initialValues
}: { initialValues?: Partial<CreateLeadPayload> | CreateLeadPayload } = {}) => {
  const {
    values,
    register,
    triggerPartialValidation,
    handleChange,
    onSubmit,
    onPartialSubmit,
    isSubmitting,
    reset,
    errors
  } = useFormTypedWithPartial<Partial<CreateLeadPayload> | CreateLeadPayload>(
    initialValues
      ? initialValues
      : {
          first_name: "",
          last_name: "",
          source_id: "",
          primary_phone: "",
          primary_email: "",
          other_phones: [],
          other_emails: [],
          message: "",
          desired_move_in_date: "",
          partner_user_id: null
        },
    validationSchema
  );

  const updatePrimaryPhone = (value: string) => {
    handleChange("primary_phone", () => value)();
  };

  const updatePrimaryEmail = (value: string) => {
    handleChange("primary_email", () => value)();
  };

  const addOtherPhone = () => {
    const newOtherPhones = [...(values.other_phones ?? []), ""];
    handleChange("other_phones", () => newOtherPhones)();
  };

  const updateOtherPhone = (index: number, value: string) => {
    const newOtherPhones = [...(values.other_phones ?? [])];
    newOtherPhones[index] = value;
    handleChange("other_phones", () => newOtherPhones)();
  };

  const removeOtherPhone = (index: number) => {
    const newOtherPhones = values.other_phones?.filter((_, i) => i !== index);
    handleChange("other_phones", () => newOtherPhones)();
  };

  const addOtherEmail = () => {
    const newOtherEmails = [...(values.other_emails ?? []), ""];
    handleChange("other_emails", () => newOtherEmails)();
  };

  const updateOtherEmail = (index: number, value: string) => {
    const newOtherEmails = [...(values.other_emails ?? [])];
    newOtherEmails[index] = value;
    handleChange("other_emails", () => newOtherEmails)();
  };

  const removeOtherEmail = (index: number) => {
    const newOtherEmails = values.other_emails?.filter((_, i) => i !== index);
    handleChange("other_emails", () => newOtherEmails)();
  };

  const setPropertyId = (id?: number | null) => {
    handleChange("property_id", () => id)();
  };
  const setUnitId = (id?: number | null) => {
    handleChange("unit_id", () => id)();
  };

  const setMessage = (message: string) => {
    handleChange("message", () => message)();
  };

  const setDesiredMoveInDate = (date: string) => {
    handleChange("desired_move_in_date", () => date)();
  };

  return {
    values,
    triggerPartialValidation,
    register,
    onSubmit,
    handleChange,
    onPartialSubmit,
    isSubmitting,
    reset,
    errors,
    updatePrimaryPhone,
    updatePrimaryEmail,
    addOtherPhone,
    updateOtherPhone,
    removeOtherPhone,
    addOtherEmail,
    updateOtherEmail,
    removeOtherEmail,
    setPropertyId,
    setUnitId,
    setMessage,
    setDesiredMoveInDate
  };
};
